import React, { useEffect, useMemo, useRef, useState } from "react";
import PaginationTable from "../../../components/Pagination";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import api from "../../../utils/axios";
import {
	codeFormatter,
	formatMoney,
	valueDateFormatter,
} from "../../../utils/formatters";
import { useMediaQuery } from "@mui/material";
import useLang from "../../../hooks/useLang";
import { ReactComponent as DriverIcon } from "../../../assets/driverIcon.svg";
import { ReactComponent as Exchange } from "../../../assets/Exchange1.svg";
import { ReactComponent as Invoice } from "../../../assets/Invoice1.svg";
import { ReactComponent as Returnable } from "../../../assets/Returnable.svg";
import DefaultDatePicker from "../../../components/form/DefaultDatePicker";
import DefaultSelect from "../../../components/form/DefaultSelect";
import {
	exportDataToCsv,
	moneyExportColumns,
} from "../../../utils/exportDataToCsv";
import useCurrencies from "../../../hooks/useCurrencies";

const MockOrders = () => {
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef<AgGridReact | null>(null);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [mockOrders, setMockOrders] = useState<any[]>([]);
	const [filteredMockOrders, setFilteredMockOrders] = useState<any[]>([]);
	const [filteredDate, setFilteredDate] = useState<any>();
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [selectValue, setSelectValue] = useState(null);
	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};
	const { strings } = useLang("filterorder");

	const fetchTableData = async () => {
		try {
			const response = await api.get("OrdersF2");
			setMockOrders(response?.data);
			setFilteredMockOrders(response?.data);
		} catch (error) {
			console.log(error);
		}
	};
	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Data loads once on open. It used to share the column effect below, so a language switch or
	// a resize reloaded the whole list.
	useEffect(() => {
		fetchTableData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!strings) {
			return;
		}
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 40,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				suppressSizeToFit: isSmallScreen,
				cellRendererFramework: (params: any) => {
					const test = codeFormatter(params.value);
					return (
						<div className="flex flex-row gap-2 items-center">
							<p>{test}</p>
							{params.data.exchangeable && (
								<div>
									<Exchange />
								</div>
							)}
							{params.data.returnable && (
								<div>
									<Returnable />
								</div>
							)}
							{params.data.invoice && (
								<div>
									<Invoice />
								</div>
							)}
						</div>
					);
				},
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 140,
			},
			{
				field: "refid",
				headerName: strings?.reference,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "statusDescription",
				headerName: strings?.status,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								{/* <p className="w-full font-medium"> */}
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
							{params.data.driverName != " " && (
								<p className="w-full flex items-center gap-1">
									<DriverIcon />
									<span className="font-normal">
										{params.data.driverName}
									</span>
								</p>
							)}
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: "Data e regjistrimit",
				valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 140,
			},
			{
				headerName: strings?.seller,
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.sellerUserName;
					const sellerNumber = params.data.businessNr;
					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + " (" + sellerUniqueId + ") ";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{sellerNumber}
							</p>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyer,
				width: 160,
				cellRendererFramework: (params: any) => {
					const buyerNameSurname = params.data.buyerNameAndSurname;
					const phone = params.data.buyerPhoneNo;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{buyerNameSurname}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{phone}
							</p>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyerPhoneNo",
				headerName: "Telefoni i beresit",
				hide: true,
			},
			{
				field: "orderDescription",
				headerName: strings?.description,
				width: 180,
				suppressSizeToFit: isSmallScreen,
				hide: true,
			},
			{
				field: "packageDescription",
				headerName: "Emertimi",
			},
			{
				field: "country",
				headerName: strings?.country,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.city,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderPrice",
				valueGetter: (params: any) =>
					// Each order carries the currency it was registered in.
					formatMoney(
						params.data.orderPrice,
						params.data.orderCurrencyId
					),
				width: 130,
				headerName: strings?.packageValueWithVAT,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: "Kosto e transportit",
				valueGetter: (params: any) =>
					// Each order carries the currency it was registered in.
					formatMoney(
						params.data.transportPrice,
						params.data.orderCurrencyId
					),
				width: 130,
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [isSmallScreen, strings]);

	const onPageChanged = (page: any) => {
		setCurrentPage(page);
	};
	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};
	const paginatedOrders = useMemo(() => {
		const dataToPaginate = filteredMockOrders;
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return dataToPaginate.slice(startIndex, endIndex);
	}, [filteredMockOrders, currentPage, pageSize]);

	const totalOrders = filteredMockOrders.length;
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, totalOrders - startOrderIndex);

	const customOptions = [
		{
			label: strings?.labels?.export_excel,
			value: "export_excel",
		},
	];

	/**
	 * Each money column, followed by the currency it is denominated in. Read per row from
	 * `orderCurrencyId`: one order on this sheet can be LEK and the next EUR.
	 */
	const orderMoneyExport = (colId: string, amountHeader?: string) =>
		moneyExportColumns({
			colId,
			// Paired with whatever the amount column is actually headed — the grid header when
			// there is one, the raw field when there is not — so the two always read as a pair.
			currencyHeaderName: `${strings?.currencyColumn} - ${
				amountHeader ?? colId
			}`,
			currency: (row: any) => row?.orderCurrencyId ?? defaultCurrencyId,
			dualCurrencyEnabled,
		});

	const selectedColumnIds = [
		"displayId",
		"refid",
		"statusDescription",
		"driverName",
		"sellerNameAndSurname",
		"sellerPhoneNo",
		"sellerUserName",
		"gatheringCity",
		"buyerNameAndSurname",
		"buyerPhoneNo",
		"buyerAddress",
		"country",
		"city",
		"orderDescription",
		"packageDescription",
		"deliveryInstructions",
		"weight",
		...orderMoneyExport("orderPrice", strings?.packageValueWithVAT),
		...orderMoneyExport("orderPriceNoVat", strings?.packageValueNoVAT),
		...orderMoneyExport("vat", strings?.vat),
		"returnable",
		...orderMoneyExport("transportPrice", strings?.transportPrice),
		...orderMoneyExport("refusedPrice", strings?.refusalPrice),
		"openable",
		"fragile",
		"createdAt",
		"pickedUpDate",
		"transportingDate",
		"deliveredDate",
		"latestStatusDate",
		"changes",
		"settlementId",
		"ditetNeTransport",
		"oretNeTransport",
		"costumsCode",
		"customsDescription",
		"origjina",
		"garancion",
		"exchangeable",
		"statusChangeComment",
	];
	const onOptionChange = (value: any) => {
		if (value === "export_excel") {
			const dataToExport = mockOrders;
			exportDataToCsv(grid, selectedColumnIds, dataToExport);
		}
	};

	const onFilterDateChange = (selectedDate: any) => {
		if (selectedDate) {
			const itemDate = new Date(selectedDate.$d)
				.toISOString()
				.split("T")[0];

			const tableData = mockOrders.filter((order: any) => {
				const orderDate = new Date(order.createdAt)
					.toISOString()
					.split("T")[0];
				return orderDate === itemDate;
			});
			setFilteredDate(selectedDate);
			setFilteredMockOrders(tableData);
		} else {
			setFilteredMockOrders(mockOrders);
			setFilteredDate(null);
		}
	};

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<div className="p-4 gap-4">
						<h1>{strings?.allOrders}</h1>
						<div className="flex flex-wrap gap-4 p-2 items-center">
							<DefaultDatePicker
								label="Data"
								value={filteredDate}
								onChange={onFilterDateChange}
								width={172}
								className="registerInput"
							/>
							<DefaultSelect
								placeholder={strings?.options}
								label={strings?.options}
								options={customOptions}
								value={selectValue}
								onChange={onOptionChange}
								flexDirection="col"
								alignCenter="start"
								className="registerInput"
								allowClear
							/>
						</div>
					</div>
					<div className="h-auto">
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={filteredMockOrders.length}
							startOrderIndex={startOrderIndex}
							endOrderIndex={endOrderIndex}
							onChange={onPageChanged}
							onShowSizeChange={handlePageSizeChange}
						/>
					</div>
					<div
						className="ag-theme-alpine"
						style={{
							width: "100%",
							height: "auto",
						}}
					>
						<AgGridReact
							rowData={paginatedOrders}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							domLayout="autoHeight"
							// onCellClicked={onCellClicked}
							suppressPaginationPanel={false}
							rowHeight={60}
							// onSelectionChanged={onSelectionChanged}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default MockOrders;
